import React, { useMemo } from 'react';
import { Form, Table } from 'react-bootstrap';
import moment from 'moment';
import { formatCurrency } from '../../../../../helpers/global';

const DATA_COLUMNS = [
  { key: 'invoiceNumber', label: 'Invoice #', width: 120 },
  { key: 'invoiceAmount', label: 'Amount', align: 'right', width: 110 },
  { key: 'invoiceDueDate', label: 'Due date', width: 130 },
  { key: 'daysOverdue', label: 'Days overdue', align: 'right', width: 110 },
  { key: 'paymentReminderSentOn', label: 'Reminder sent', width: 140 }
];

const SELECT_COLUMN = { key: 'select', label: '', width: 36 };

const thClassName = column =>
  ['align-middle text-nowrap text-uppercase font-weight-medium text-muted', column.align === 'right' && 'text-right']
    .filter(Boolean)
    .join(' ');

const tdClassName = column =>
  ['align-middle', column.align === 'right' && 'text-right'].filter(Boolean).join(' ');

const cellStyle = column => ({
  width: column.width,
  minWidth: column.width,
  maxWidth: column.width,
  padding: '8px',
  verticalAlign: 'top'
});

const formatDate = value => (value ? moment(value).format('MMM D, YYYY') : '—');

const PaymentReminderInvoiceTable = ({
  invoices = [],
  loading = false,
  selectedIds = [],
  showSelect = false,
  disabled = false,
  onToggleSelect,
  onToggleSelectAll
}) => {
  const columns = useMemo(
    () => (showSelect ? [SELECT_COLUMN, ...DATA_COLUMNS] : DATA_COLUMNS),
    [showSelect]
  );
  const allSelected = invoices.length > 0 && selectedIds.length === invoices.length;
  const someSelected = selectedIds.length > 0 && selectedIds.length < invoices.length;

  const renderDataCell = (column, row) => {
    if (column.key === 'invoiceNumber') {
      return <span className="font-weight-semibold">{row.invoiceNumber || '—'}</span>;
    }
    if (column.key === 'invoiceAmount') return formatCurrency(row.invoiceAmount);
    if (column.key === 'invoiceDueDate') return formatDate(row.invoiceDueDate);
    if (column.key === 'daysOverdue') return row.daysOverdue ?? 0;
    if (column.key === 'paymentReminderSentOn') {
      return row.paymentReminderSentOn ? moment(row.paymentReminderSentOn).format('MMM D, YYYY') : '—';
    }
    return null;
  };

  return (
    <div className="position-relative">
      <div className="mb-2 d-flex flex-wrap align-items-center justify-content-between" style={{ gap: 6 }}>
        <p className="mb-0 midFont">Past-due unpaid invoices</p>
        {!loading && invoices.length > 0 && (
          <span className="xsmallFont text-muted">{invoices.length} invoice{invoices.length === 1 ? '' : 's'}</span>
        )}
      </div>

      {!loading && invoices.length === 0 ? (
        <p className="text-muted midFont mb-0">No past-due unpaid invoices found for this order.</p>
      ) : (
        <div className="table-responsive mb-0 border overflow-hidden" style={{ borderRadius: 8 }}>
          <Table size="sm" className="mb-0 midFont" style={{ borderCollapse: 'collapse', tableLayout: 'fixed' }}>
            <thead>
              <tr className="bg-muted">
                {columns.map(column => (
                  <th
                    key={column.key}
                    className={thClassName(column)}
                    style={{ ...cellStyle(column), fontSize: 11, letterSpacing: '0.03em' }}
                  >
                    {column.key === 'select' ? (
                      <Form.Check
                        type="checkbox"
                        checked={allSelected}
                        ref={el => {
                          if (el) el.indeterminate = someSelected;
                        }}
                        disabled={disabled || loading || invoices.length === 0}
                        onChange={e => onToggleSelectAll?.(e.target.checked)}
                        aria-label="Select all invoices"
                      />
                    ) : (
                      column.label
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody aria-busy={loading || undefined}>
              {loading
                ? Array.from({ length: 2 }, (_, rowIndex) => (
                    <tr key={`skeleton-${rowIndex}`} className="border-top">
                      {columns.map(column => (
                        <td key={column.key} className={tdClassName(column)} style={cellStyle(column)}>
                          <div className="shimmer shimmer-line" style={{ borderRadius: 10, height: 28 }} />
                        </td>
                      ))}
                    </tr>
                  ))
                : invoices.map(row => {
                    const id = String(row._id);
                    const selected = selectedIds.includes(id);
                    return (
                      <tr key={id} className="border-top">
                        {columns.map(column => (
                          <td key={column.key} className={tdClassName(column)} style={cellStyle(column)}>
                            {column.key === 'select' ? (
                              <Form.Check
                                type="checkbox"
                                checked={selected}
                                disabled={disabled}
                                onChange={() => onToggleSelect?.(id)}
                                aria-label={`Select invoice ${row.invoiceNumber || id}`}
                              />
                            ) : (
                              renderDataCell(column, row)
                            )}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default PaymentReminderInvoiceTable;
